import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { SapEnvironment } from 'src/app/models';
import {
  bomConfigurationFactory,
  colourApiConfigurationFactory,
  connectorApiConfigurationFactory,
  constructionApiConfigurationFactory,
  designApiConfigurationFactory,
  documentApiConfigurationFactory,
  equipmentApiConfigurationFactory,
  familyTreeApiConfigurationFactory,
  infoCodesApiConfigurationFactory,
  itemLogApiConfigurationFactory,
  longTextConfigurationFactory,
  materialDataSetConfigurationFactory,
  materialWhereUsedApiConfigurationFactory,
  multiColourConfigurationFactory,
  notificationApiConfigurationFactory,
  orderApiConfigurationFactory,
  partnerConfigurationFactory,
  projectApiConfigurationFactory,
  taskListApiConfigurationFactory,
  uomConfigurationFactory,
  userMaterialConfigurationFactory,
} from 'src/app/modules/sap-api-factories';
import { SessionService } from 'src/app/state';
import { environment } from 'src/environments/environment';
import { sapDevProxyUrl, sapQaProxyUrl, sapZProxyUrl } from 'src/environments/environment.base';
import { sapProxyUrl } from 'src/environments/environment.prod';
import { BomApiModule } from 'src/generated-clients/bom';
import { ColourApiModule } from 'src/generated-clients/colour';
import { ConnectorApiModule } from 'src/generated-clients/connector';
import { ConstructionApiModule } from 'src/generated-clients/construction';
import { ConstructionCdsApiModule } from 'src/generated-clients/construction_cds';
import { DesignApiModule } from 'src/generated-clients/design';
import { DocumentApiModule } from 'src/generated-clients/document';
import { FamilyTreeApiModule } from 'src/generated-clients/familytree';
import { InfoCodesApiModule } from 'src/generated-clients/infocodes';
import { ItemLogApiModule } from 'src/generated-clients/itemlog';
import { LongTextApiModule } from 'src/generated-clients/longtext';
import { MaterialApiModule } from 'src/generated-clients/material';
import { MaterialWhereUsedApiModule } from 'src/generated-clients/material_whereused';
import { MultiColourApiModule } from 'src/generated-clients/multicolour';
import { NotificationApiModule } from 'src/generated-clients/notification';
import { OrderApiModule } from 'src/generated-clients/order';
import { PartnerApiModule } from 'src/generated-clients/partner';
import { ProjectApiModule } from 'src/generated-clients/project';
import { TaskListApiModule } from 'src/generated-clients/tasklist';
import { UomApiModule } from 'src/generated-clients/uom';
import { UserMaterialApiModule } from 'src/generated-clients/user_material';

@NgModule({
  imports: [
    ColourApiModule.forRoot(colourApiConfigurationFactory),
    ConnectorApiModule.forRoot(connectorApiConfigurationFactory),
    ConstructionApiModule.forRoot(constructionApiConfigurationFactory),
    DesignApiModule.forRoot(designApiConfigurationFactory),
    FamilyTreeApiModule.forRoot(familyTreeApiConfigurationFactory),
    InfoCodesApiModule.forRoot(infoCodesApiConfigurationFactory),
    ItemLogApiModule.forRoot(itemLogApiConfigurationFactory),
    NotificationApiModule.forRoot(notificationApiConfigurationFactory),
    OrderApiModule.forRoot(orderApiConfigurationFactory),
    ProjectApiModule.forRoot(projectApiConfigurationFactory),
    TaskListApiModule.forRoot(taskListApiConfigurationFactory),
    MaterialWhereUsedApiModule.forRoot(materialWhereUsedApiConfigurationFactory),
    DocumentApiModule.forRoot(documentApiConfigurationFactory),
    PartnerApiModule.forRoot(partnerConfigurationFactory),
    MultiColourApiModule.forRoot(multiColourConfigurationFactory),
    UomApiModule.forRoot(uomConfigurationFactory),
    UserMaterialApiModule.forRoot(userMaterialConfigurationFactory),
    LongTextApiModule.forRoot(longTextConfigurationFactory),
    MaterialApiModule.forRoot(materialDataSetConfigurationFactory),
    BomApiModule.forRoot(bomConfigurationFactory),
    ConstructionCdsApiModule.forRoot(equipmentApiConfigurationFactory),
  ],
})
export class SapApiModule {
  public static sapUrl: string;

  public static forRoot(): ModuleWithProviders<SapApiModule> {
    return {
      ngModule: SapApiModule,
    };
  }

  public constructor(
    private readonly sessionService: SessionService,
    @Optional() @SkipSelf() parentModule?: SapApiModule
  ) {
    if (parentModule) {
      throw new Error('SapApiModule is already loaded. Import in your base AppModule only.');
    }

    let sapUrl = environment.sapUrl;

    const isDevOrLocal = this.sessionService.isDevOrLocal();

    if (isDevOrLocal) {
      const sapEnvironment = this.sessionService.getSelectedSapEnvironment();

      switch (sapEnvironment) {
        case SapEnvironment.P:
          sapUrl = sapProxyUrl;
          break;

        case SapEnvironment.D:
          sapUrl = sapDevProxyUrl;
          break;

        case SapEnvironment.Q:
          sapUrl = sapQaProxyUrl;
          break;

        case SapEnvironment.Z:
          sapUrl = sapZProxyUrl;
          break;

        default:
          throw new Error('Sap environment should be either D or Z when running on DEV/LOCAL');
      }
    }

    SapApiModule.sapUrl = sapUrl;
  }
}
